import React from 'react';
import moment from 'moment';
import 'moment/locale/id';
import AppointmentDialog from './AppointmentDialog';
import { STATUS_COLORS, statusColor, statusTint, statusLabel } from './shared/appointmentStatus';

const MAX_CHIPS_PER_DAY = 3;

export default class AppointmentCalendar extends React.Component {
  constructor(props) {
    super(props);
    // Admin runs under /id and /en; month and weekday names have to follow.
    moment.locale(props.locale === 'id' ? 'id' : 'en');
    this.state = {
      cursor: moment().startOf(props.mode || 'month'),
      byDate: {},
      loading: true,
      error: false,
      openDay: null,
      selectedAppointment: null,
    };
  }

  componentDidMount() {
    this.load();
    document.addEventListener('keydown', this.handleEscape);
  }

  // The page owns the range mode and the filters; both change the feed, so the grid
  // refetches when either does. The doctor tab passes neither and never gets here.
  componentDidUpdate(prevProps) {
    const { mode, filters } = this.props;

    if (prevProps.mode !== mode) {
      this.setState(
        prev => ({ cursor: prev.cursor.clone().startOf(mode || 'month') }),
        this.load
      );
      return;
    }

    if (JSON.stringify(prevProps.filters) !== JSON.stringify(filters)) this.load();
  }

  componentWillUnmount() {
    this.unmounted = true;
    document.removeEventListener('keydown', this.handleEscape);
  }

  handleEscape = (event) => {
    if (event.key === 'Escape' && this.state.openDay) this.setState({ openDay: null });
  };

  // month | week | day. Everything that differs between the three -- the cursor step,
  // the grid shape and the title -- derives from this one value.
  unit = () => this.props.mode || 'month';

  // The grid feed and the export link describe the same window and the same filters,
  // so both build their query here -- an export that drifts from what is on screen is
  // a bug report waiting to happen.
  queryParams = () => {
    const { aggregate, filters } = this.props;
    const params = new URLSearchParams();

    if (aggregate) {
      // The grid shows trailing and leading days from adjacent months, so the range
      // has to cover the visible cells, not the calendar month. `end` is exclusive.
      const rows = this.weeks();
      const first = rows[0][0];
      const last = rows[rows.length - 1][rows[rows.length - 1].length - 1];
      params.set('start', first.format('YYYY-MM-DD'));
      params.set('end', last.clone().add(1, 'day').format('YYYY-MM-DD'));
    } else {
      params.set('month', this.state.cursor.format('YYYY-MM'));
    }

    const hospitalId = (filters && filters.hospital_id) || this.props.hospital_id;
    const doctorId = (filters && filters.doctor_id) || this.props.doctor_id;
    if (doctorId) params.set('doctor_id', doctorId);
    if (hospitalId) params.set('hospital_id', hospitalId);
    if (filters && filters.q) params.set('q', filters.q);

    return params;
  };

  url = () => `/api/appointments/${this.props.aggregate ? 'calendar' : 'month'}?${this.queryParams()}`;

  exportUrl = () => `/api/appointments/export?${this.queryParams()}`;

  load = () => {
    const { aggregate } = this.props;

    this.setState({ loading: true, error: false });
    fetch(this.url())
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(result => {
        if (this.unmounted) return;
        const byDate = aggregate
          ? this.groupAggregates(result.days)
          : this.groupByDate(result.appointments.data);
        this.setState({ byDate, loading: false });
      })
      .catch(() => {
        if (this.unmounted) return;
        this.setState({ loading: false, error: true, byDate: {} });
      });
  };

  groupByDate = (rows) => rows.reduce((acc, row) => {
    const key = row.attributes.date_key;
    if (!key) return acc;
    (acc[key] = acc[key] || []).push(row);
    return acc;
  }, {});

  // The calendar feed sends flat attribute hashes; wrapping them back into the
  // { id, attributes } shape lets renderChip and AppointmentDialog stay as they are.
  groupAggregates = (days) => (days || []).reduce((acc, day) => {
    acc[day.date] = {
      total: day.total,
      sample: (day.sample || []).map(attributes => ({ id: attributes.id, attributes })),
    };
    return acc;
  }, {});

  changeRange = (delta) => {
    this.setState(
      prev => ({ cursor: prev.cursor.clone().add(delta, this.unit()), openDay: null }),
      this.load
    );
  };

  goToday = () => {
    this.setState({ cursor: moment().startOf(this.unit()), openDay: null }, this.load);
  };

  total = () => Object.values(this.state.byDate).reduce(
    (sum, entry) => sum + (this.props.aggregate ? entry.total : entry.length),
    0
  );

  title = () => {
    const { cursor } = this.state;
    if (this.unit() === 'day') return cursor.format('dddd, D MMMM YYYY');
    if (this.unit() === 'week') {
      const end = cursor.clone().endOf('week');
      return `${cursor.format('D MMM')} - ${end.format('D MMM YYYY')}`;
    }
    return cursor.format('MMMM YYYY');
  };

  // Month: six rows always, so the grid height does not jump between months.
  // Week and day request a different range but reuse the very same cell renderer.
  weeks = () => {
    const { cursor } = this.state;
    if (this.unit() === 'day') return [[cursor.clone()]];

    const day = cursor.clone().startOf(this.unit() === 'week' ? 'week' : 'month').startOf('week');
    return Array.from({ length: this.unit() === 'week' ? 1 : 6 }, () =>
      Array.from({ length: 7 }, () => {
        const current = day.clone();
        day.add(1, 'day');
        return current;
      })
    );
  };

  // A single-day grid has one column, so it gets that day's name rather than the week.
  weekdayNames = () => (
    this.unit() === 'day' ? [this.state.cursor.format('ddd')] : moment.weekdaysShort(true)
  );

  renderChip = (appointment) => {
    const { attributes } = appointment;
    const color = STATUS_COLORS[attributes.status] || STATUS_COLORS.default;

    return (
      <button
        type="button"
        key={appointment.id}
        className="appointment-calendar__chip"
        style={{ borderLeftColor: color }}
        title={`${attributes.time_label || ''} ${attributes.name} - ${statusLabel(attributes.status)}`.trim()}
        onClick={() => this.setState({ selectedAppointment: appointment })}
      >
        <span className="appointment-calendar__chip-dot" style={{ backgroundColor: color }} />
        {attributes.time_label && (
          <span className="appointment-calendar__chip-time">{attributes.time_label}</span>
        )}
        <span className="appointment-calendar__chip-name">{attributes.name}</span>
      </button>
    );
  };

  cellClasses = (day) => {
    const classes = ['appointment-calendar__cell'];
    // Only a month grid has neighbouring-month days to dim.
    if (this.unit() === 'month' && !day.isSame(this.state.cursor, 'month')) {
      classes.push('appointment-calendar__cell--outside');
    }
    if (day.isSame(moment(), 'day')) classes.push('appointment-calendar__cell--today');
    return classes.join(' ');
  };

  // Aggregate cells are the click target for the day modal, so the whole cell is one
  // button and the chips inside are static -- no interactive elements nested in it.
  renderAggregateCell = (day, key) => {
    const entry = this.state.byDate[key];
    const outside = this.unit() === 'month' && !day.isSame(this.state.cursor, 'month');
    const total = entry && !outside ? entry.total : 0;
    const sample = entry && !outside ? entry.sample : [];
    const hidden = total - sample.length;

    return (
      <button
        type="button"
        className={this.cellClasses(day)}
        key={key}
        role="gridcell"
        disabled={total === 0}
        onClick={() => this.props.onDayClick(key)}
      >
        <span className="appointment-calendar__cell-head">
          <span className="appointment-calendar__date">{day.date()}</span>
          {total > 0 && <span className="appointment-calendar__count">{total}</span>}
        </span>
        <span className="appointment-calendar__chips">
          {sample.map(appointment => {
            const { attributes } = appointment;
            const color = statusColor(attributes.status);

            return (
              <span
                key={appointment.id}
                className="appointment-calendar__chip appointment-calendar__chip--static"
                style={{ backgroundColor: statusTint(attributes.status), color }}
              >
                <span className="appointment-calendar__chip-dot" style={{ backgroundColor: color }} />
                {attributes.time_label && (
                  <span className="appointment-calendar__chip-time">{attributes.time_label}</span>
                )}
                <span className="appointment-calendar__chip-name">{attributes.name}</span>
              </span>
            );
          })}
          {hidden > 0 && (
            <span className="appointment-calendar__more">
              {this.props.labels.more.replace('%{count}', hidden)}
            </span>
          )}
        </span>
      </button>
    );
  };

  // The dates are known before the feed answers, so a loading grid keeps its
  // numbers and shimmers where the chips will land instead of showing the
  // previous range's rows under the new title.
  renderSkeletonCell = (day, key) => (
    <div className={this.cellClasses(day)} key={key} role="gridcell" aria-hidden="true">
      <div className="appointment-calendar__date">{day.date()}</div>
      <div className="appointment-calendar__chips">
        <span className="appointment-calendar__skeleton-line w-75" />
        <span className="appointment-calendar__skeleton-line w-50" />
      </div>
    </div>
  );

  renderCell = (day) => {
    const key = day.format('YYYY-MM-DD');
    if (this.state.loading) return this.renderSkeletonCell(day, key);
    if (this.props.aggregate) return this.renderAggregateCell(day, key);

    const dayAppointments = this.state.byDate[key] || [];
    const visible = dayAppointments.slice(0, MAX_CHIPS_PER_DAY);
    const hidden = dayAppointments.length - visible.length;

    return (
      <div className={this.cellClasses(day)} key={key} role="gridcell">
        <div className="appointment-calendar__date">{day.date()}</div>
        <div className="appointment-calendar__chips">
          {visible.map(this.renderChip)}
          {hidden > 0 && (
            <button
              type="button"
              className="appointment-calendar__more"
              // Opens the day panel rather than growing the cell, so row
              // heights stay put no matter how busy a day is.
              onClick={() => this.setState({ openDay: key })}
            >
              {this.props.labels.more.replace('%{count}', hidden)}
            </button>
          )}
        </div>
      </div>
    );
  };

  // The page replaces this inline panel with AppointmentDayModal; the doctor tab has
  // no modal, so it keeps the panel.
  renderDayPanel() {
    const { openDay, byDate } = this.state;
    if (!openDay || this.props.aggregate) return null;

    const { labels } = this.props;
    const heading = labels.day_appointments.replace(
      '%{date}',
      moment(openDay).format('D MMMM YYYY')
    );

    return (
      <div className="appointment-calendar__day" role="dialog" aria-label={heading}>
        <div className="appointment-calendar__day-header">
          <h5 className="appointment-calendar__day-title">{heading}</h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-primary"
            onClick={() => this.setState({ openDay: null })}
          >
            {labels.close}
          </button>
        </div>
        <div className="appointment-calendar__day-list">
          {(byDate[openDay] || []).map(this.renderChip)}
        </div>
      </div>
    );
  }

  render() {
    const { labels, aggregate, toolbarExtra } = this.props;
    const { loading, error, selectedAppointment } = this.state;
    const empty = !loading && !error && this.total() === 0;

    return (
      <div className={`appointment-calendar${aggregate ? ' appointment-calendar--aggregate' : ''}`}>
        <div className="appointment-calendar__toolbar">
          {/* Arrows, then the range title, then Hari ini; the mode switch is pushed
              to the far end. */}
          <div className="appointment-calendar__header">
            <div className="appointment-calendar__nav">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                aria-label={labels.previous_month}
                title={labels.previous_month}
                onClick={() => this.changeRange(-1)}
              >
                <i className="zmdi zmdi-chevron-left" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                aria-label={labels.next_month}
                title={labels.next_month}
                onClick={() => this.changeRange(1)}
              >
                <i className="zmdi zmdi-chevron-right" aria-hidden="true" />
              </button>
            </div>
            <h4 className="appointment-calendar__title">{this.title()}</h4>
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={this.goToday}>
              {labels.today}
            </button>
            {/* Sits in the free space between Hari ini and the switch, so neither
                moves when it fills in. */}
            <span className="appointment-calendar__status" role="status">
              {loading && labels.loading}
              {error && <span className="text-danger">{labels.load_error}</span>}
            </span>
            {this.props.viewSwitch}
            {/* Only the full page exports; the doctor tab has no filters to carry. */}
            {aggregate && (
              <a className="btn btn-outline-primary btn-sm" href={this.exportUrl()}>
                <i className="zmdi zmdi-download mr-2" aria-hidden="true" />
                {labels.export}
              </a>
            )}
          </div>

          {toolbarExtra}
        </div>

        <div className="appointment-calendar__body">
          <div
            className={`appointment-calendar__grid${this.unit() === 'day' ? ' appointment-calendar__grid--day' : ''}`}
            role="grid"
            aria-busy={loading}
          >
            {this.weekdayNames().map(name => (
              <div className="appointment-calendar__weekday" role="columnheader" key={name}>{name}</div>
            ))}
            {this.weeks().map(week => week.map(this.renderCell))}
          </div>
          {empty && (
            <p className="appointment-calendar__empty">
              {aggregate ? labels.no_appointments_range : labels.no_appointments}
            </p>
          )}
          {this.renderDayPanel()}
        </div>

        <AppointmentDialog
          open={Boolean(selectedAppointment)}
          onClose={() => this.setState({ selectedAppointment: null })}
          appointment={selectedAppointment}
        />
      </div>
    );
  }
}
